import { makeAutoObservable, runInAction } from 'mobx'
import { decorate, inject, injectable } from 'inversify'
import { uniqBy } from 'lodash'

// Evertel
import { Api } from '@evertel/api'
import { SessionService } from '@evertel/session/SessionService'

// Evertel Types
import type { APIDataAgreementAcceptance } from '@evertel/types'

interface AgreementAcceptanceItem {
    id: number
    departmentAgreementId: number
    agreementDocumentId: number
    title: string
    version: number | null
    isLatest: boolean
    roomName: string | null
    acceptedAt: Date | null
}

// Documents are append-only (every edit inserts a new row with a bumped version), and
// DepartmentAgreement.agreementDocumentId always points at the newest one. So an acceptance
// is up to date iff it points at the same document the department agreement currently does.
// Shared here so web and mobile can't drift on how "outdated" is determined.
const isLatestVersion = (acceptance: APIDataAgreementAcceptance) =>
    acceptance.agreementDocumentId === acceptance.departmentAgreement?.agreementDocumentId

interface AgreementAcceptanceSection {
    departmentId: number
    departmentName: string
    data: AgreementAcceptanceItem[]
}

class AgreementAcceptanceController {
    acceptances: APIDataAgreementAcceptance[] = []
    acceptancesCount = 0
    isLoading = false

    // Grouping by department is incompatible with skip/limit paging (a page boundary
    // would split a department across pages), so this controller fetches every row and
    // groups client-side. The limit is only an artificial per-request cap to keep query
    // size in line with our other endpoints; we page internally until all rows are loaded.
    private limit = 100

    constructor(
        private api: Api,
        private sessions: SessionService
    ) {
        makeAutoObservable(this)
    }

    reset() {
        this.acceptances = []
        this.acceptancesCount = 0
        this.isLoading = false
    }

    fetch = async () => {
        const userId = this.sessions.currentUserId
        if (!userId) return

        this.reset()
        this.isLoading = true

        try {
            const count = (await this.api.Routes.BlueUser.getAgreementsAcceptanceCount(userId, this.where)).count ?? 0

            runInAction(() => {
                this.acceptancesCount = count
            })

            let page = 1
            const all: APIDataAgreementAcceptance[] = []

            // Fetch every row, one capped request at a time, until we have them all.
            while (all.length < count) {
                const results = await this.api.Routes.BlueUser.getAgreementsAcceptance(userId, this.filter(page))

                if (!results?.length) break

                all.push(...results)
                page++
            }

            runInAction(() => {
                this.acceptances = uniqBy(all, 'id')
            })
        } catch (err) {
            console.error('AgreementAcceptance fetch failed', err)
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }

    fetchAcceptedDocument = async (acceptanceId: number) => {
        const userId = this.sessions.currentUserId
        if (!userId) return null

        const results = await this.api.Routes.BlueUser.getAgreementsAcceptance(userId, {
            where: { id: acceptanceId },
            include: [
                { relation: 'agreementDocument', scope: { fields: ['id', 'name', 'content', 'version', 'createdDate'] } }
            ],
            limit: 1
        })

        return results?.[0]?.agreementDocument ?? null
    }

    get where() {
        return {}
    }

    filter(page: number) {
        return {
            where: this.where,
            fields: ['id', 'departmentId', 'departmentAgreementId', 'agreementDocumentId', 'roomId', 'createdDate'],
            include: [
                { relation: 'agreementDocument', scope: { fields: ['id', 'name', 'version'] } },
                { relation: 'room', scope: { fields: ['id', 'name'] } },
                { relation: 'department', scope: { fields: ['id', 'name'] } },
                { relation: 'departmentAgreement', scope: { fields: ['id', 'deletedDate', 'agreementDocumentId'] } }
            ],
            order: 'departmentId ASC, createdDate DESC',
            limit: this.limit,
            skip: (page - 1) * this.limit
        }
    }

    // Grouped by department for the SectionList. Soft-deleted agreements come back with
    // departmentAgreement === null (SoftDelete mixin), so drop them here.
    get agreementsByDepartment(): AgreementAcceptanceSection[] {
        const groups = new Map<number, AgreementAcceptanceSection>()

        for (const acceptance of this.acceptances) {
            if (!acceptance.departmentAgreement) continue

            let group = groups.get(acceptance.departmentId)

            if (!group) {
                group = {
                    departmentId: acceptance.departmentId,
                    departmentName: acceptance.department?.name ?? '',
                    data: []
                }
                groups.set(acceptance.departmentId, group)
            }

            group.data.push({
                id: acceptance.id,
                departmentAgreementId: acceptance.departmentAgreementId,
                agreementDocumentId: acceptance.agreementDocumentId,
                title: acceptance.agreementDocument?.name ?? '',
                version: acceptance.agreementDocument?.version ?? null,
                isLatest: isLatestVersion(acceptance),
                roomName: acceptance.room?.name ?? null,
                acceptedAt: acceptance.createdDate ?? null
            })
        }

        return [...groups.values()]
    }

}

decorate(injectable(), AgreementAcceptanceController)
decorate(inject(Api), AgreementAcceptanceController, 0)
decorate(inject(SessionService), AgreementAcceptanceController, 1)

export { AgreementAcceptanceController, isLatestVersion }
export type { AgreementAcceptanceItem, AgreementAcceptanceSection }
